import React from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, subDays } from 'date-fns';

import { usePayerOptions } from './hooks/use-payer-options';
import { usePatientList } from './hooks/use-patient-list';
import { useDocumentSummary } from './hooks/use-document-summary';
import { useTherapistOptions } from './hooks/use-therapist-options';
import { useTherapistScopeCounts } from './hooks/use-therapist-scope-counts';
import { TherapistSelect, TherapistScopeToggle } from './by-cohort/therapist-control';
import { GroupPicker } from './by-cohort/group-picker';
import { HeldFromBillingToggle } from './held-from-billing-toggle';
import { MatchedRoster } from './by-cohort/matched-roster';
import { TherapistScope } from './by-cohort/types';
import { PacketSummary } from './by-patient/packet-summary';
import { ReviewAndRequestDialog } from './by-patient/review-and-request-dialog';
import { DEFAULT_PACKAGING, PackagingChoices, estimateBytes } from './by-patient/packaging';
import { JobRequestDetails } from './mock-data';
import type { PendingJob } from './by-patient-panel';

interface Props {
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>, pending: PendingJob) => void;
}

const Step: React.FC<{ n: number; title: string; hint?: string; children: React.ReactNode }> = ({
  n,
  title,
  hint,
  children,
}) => (
  <Box>
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      sx={{ mb: 1.5 }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600 }}
      >
        {n}. {title}
      </Typography>
      {hint && (
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {hint}
        </Typography>
      )}
    </Stack>
    {children}
  </Box>
);

const safeName = (value: string) => value.replace(/[^\w-]+/g, '_').replace(/_+/g, '_');

/**
 * SQ-173 — By cohort: WHEN, then WHICH RESIDENTS, then WHAT, then WHO MATCHED.
 *
 * The order is the opposite of By patient for a reason. Here you do not know the
 * residents — that is the point of the mode — so the population attributes come first and
 * the roster falls out at the bottom where it can be trimmed. It renders above the submit
 * button, not below it, which is the layout fault that drove the whole redesign (A3, A5).
 *
 * Nothing the current screen can do is removed; what changes is the order, the labels, and
 * two controls that switch each other off.
 */
export const ByCohortPanel: React.FC<Props> = ({ submitting, onSubmit }) => {
  //  Cohort keeps its own date default. "Earliest document in the facility" would be 2015
  //  and useless here, where By patient uses the resident's own record (C4).
  const [dateFrom, setDateFrom] = React.useState<Date>(() => subDays(new Date(), 30));
  const [dateTo, setDateTo] = React.useState<Date>(() => new Date());
  const [wholeStay, setWholeStay] = React.useState(false);
  const [payerType, setPayerType] = React.useState('');
  const [payerName, setPayerName] = React.useState('');
  const [therapistUuid, setTherapistUuid] = React.useState('');
  const [scope, setScope] = React.useState<TherapistScope>('author');
  const [deselectedKinds, setDeselectedKinds] = React.useState<Set<string>>(new Set());
  const [deselectedPatients, setDeselectedPatients] = React.useState<Set<string>>(new Set());
  const [choices, setChoices] = React.useState<PackagingChoices>(DEFAULT_PACKAGING);
  const [fileName, setFileName] = React.useState('');
  const [fileNameEdited, setFileNameEdited] = React.useState(false);
  const [adrReference, setAdrReference] = React.useState('');
  const [reviewOpen, setReviewOpen] = React.useState(false);
  //  SQ-181: ordinary state. Every request starts excluding held documents — THIN-8113 is
  //  explicit that this must not persist, so that an internal review cannot silently set
  //  up the next external submission to over-disclose.
  const [includeHeld, setIncludeHeld] = React.useState(false);

  //  "Include each resident's whole stay" is a bulk expansion, not a scope: there is no
  //  per-patient episode to pick across ninety-odd people (C7). It rides the same backend
  //  branch the old Full episode button used.
  const dateScope = wholeStay ? 'episode' : 'range';

  const { payerOptions, payerNames } = usePayerOptions(dateFrom, dateTo);
  const { therapists, loading: therapistsLoading } = useTherapistOptions(dateFrom, dateTo);

  const baseFilters = {
    dateFrom,
    dateTo,
    payerType,
    payerName,
    disciplines: [] as string[],
    docTypes: [] as string[],
    dateScope,
    therapistId: therapistUuid || undefined,
    therapistScope: therapistUuid ? scope : undefined,
    includeHeldFromBilling: includeHeld,
  };

  const { patients, loading: patientsLoading } = usePatientList(baseFilters);
  const { summary, loading: summaryLoading } = useDocumentSummary({
    ...baseFilters,
    selectedPatientIds: [],
  });

  //  Both numbers for the toggle, fetched independently of the summary. Routing them
  //  through useDocumentSummary put three callers on one Redux slice, so all three read
  //  whichever reply landed last — on screen, both sides showed the same number and
  //  followed the selected tab.
  const scopeCounts = useTherapistScopeCounts({
    dateFrom,
    dateTo,
    payerType,
    payerName,
    dateScope,
    therapistId: therapistUuid,
  });

  // ── Cascade ──

  //  Payer name is dead while payer type is All — and the data says why: usePayerOptions
  //  returns names keyed BY type, so there is no such thing as a plan list across all of
  //  them. Offering one is what produced "I searched Managed A but it showed me Med A and
  //  Medicaid pts also".
  const payerNameDisabled = !payerType;
  React.useEffect(() => {
    if (payerNameDisabled && payerName) setPayerName('');
  }, [payerNameDisabled, payerName]);

  //  A therapist who leaves the window takes their scope with them.
  React.useEffect(() => {
    if (!therapistUuid) setScope('author');
  }, [therapistUuid]);

  React.useEffect(() => {
    setDeselectedPatients(new Set());
    setDeselectedKinds(new Set());
  }, [dateFrom, dateTo, payerType, payerName, therapistUuid, scope, wholeStay]);

  const groups = React.useMemo(() => summary?.groups ?? [], [summary]);

  const selectedPatients = React.useMemo(
    () => patients.filter((p) => !deselectedPatients.has(p.patient_id)),
    [patients, deselectedPatients]
  );

  //  The roster count and the submit count are the same number. SQ-156 and SQ-158 make
  //  that true at the source; this just does not reintroduce a second one.
  const selectedDocuments = React.useMemo(
    () =>
      groups.filter((g) => !deselectedKinds.has(g.kind)).reduce((n, g) => n + g.documentCount, 0),
    [groups, deselectedKinds]
  );

  const periodLabel = `${format(dateFrom, 'd MMM yyyy')} – ${format(dateTo, 'd MMM yyyy')}`;

  React.useEffect(() => {
    if (fileNameEdited) return;
    const parts = [safeName(periodLabel)];
    if (payerName) parts.push(safeName(payerName));
    else if (payerType) parts.push(safeName(payerType));
    setFileName(parts.join('_'));
  }, [periodLabel, payerType, payerName, fileNameEdited]);

  const handleRequest = () => {
    const requestDetails: JobRequestDetails = {
      filters_applied: {
        date_from: format(dateFrom, 'yyyy-MM-dd'),
        date_to: format(dateTo, 'yyyy-MM-dd'),
        ...(payerType && { payer_type: payerType }),
        ...(payerName && { payer_name: payerName }),
        patient_ids: selectedPatients.map((p) => p.patient_id),
      },
      file_organization: {
        date_scope: dateScope,
        group_by: choices.groupBy,
        document_handling: choices.documentHandling,
        order_by: choices.orderBy,
        deliver_as: choices.deliverAs,
      },
      ...(adrReference.trim() && { adr_reference: adrReference.trim() }),
    };

    onSubmit(
      {
        job_name: fileName,
        filters: {
          date_from: format(dateFrom, 'yyyy-MM-dd'),
          date_to: format(dateTo, 'yyyy-MM-dd'),
          ...(payerType && { payer_type: payerType }),
          ...(payerName && { payer_name: payerName }),
          //  Cohort stays filter-driven: the roster can be hundreds of ids and the
          //  resolver already reproduces it. Only a trimmed roster travels explicitly.
          ...(deselectedPatients.size > 0 && {
            patient_ids: selectedPatients.map((p) => p.patient_id),
          }),
          ...(adrReference.trim() && { adr_reference: adrReference.trim() }),
          ...(therapistUuid && { therapist_id: therapistUuid, therapist_scope: scope }),
          // SQ-181: cohort resolves at submit, so the job must carry what the roster was
          // drawn with or it delivers a different set from the one on screen.
          ...(includeHeld && { include_held_from_billing: true }),
        },
        packaging_options: {
          date_scope: dateScope,
          deliver_as: choices.deliverAs,
          group_by: choices.groupBy,
          document_handling: choices.documentHandling,
          order_by: choices.orderBy,
          ...(choices.includeAuditLog && { include_audit_log: true }),
        },
      },
      {
        documentCount: selectedDocuments,
        patientCount: selectedPatients.length,
        sizeBytes: estimateBytes(selectedDocuments),
        requestDetails,
      }
    );
    setReviewOpen(false);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      alignItems="flex-start"
    >
      <Stack
        spacing={3}
        divider={<Divider flexItem />}
        sx={{
          flex: { md: '0 0 calc(60% - 12px)' },
          width: '100%',
          minWidth: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2.5,
        }}
      >
        <Step
          n={1}
          title="Dates"
          // hint="defaults to the last 30 days"
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ maxWidth: 460 }}
            >
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={(d) => d && setDateFrom(d)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="Through"
                value={dateTo}
                onChange={(d) => d && setDateTo(d)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={wholeStay}
                  onChange={(e) => setWholeStay(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Include each resident&apos;s whole stay, not just these dates
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Ignores the dates above and takes everything from their admission onward.
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </Step>

        <Step
          n={2}
          title="Which patients"
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
            >
              <TextField
                select
                size="small"
                fullWidth
                label="Payer type"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                value={payerType}
                onChange={(e) => setPayerType(e.target.value)}
              >
                <MenuItem value="">Any</MenuItem>
                {payerOptions.map((o: any) => (
                  <MenuItem
                    key={o.value ?? o}
                    value={o.value ?? o}
                  >
                    {o.label ?? o}
                  </MenuItem>
                ))}
              </TextField>

              <Tooltip title={payerNameDisabled ? 'Choose a payer type first' : ''}>
                <span style={{ width: '100%' }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Payer name"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                    value={payerName}
                    disabled={payerNameDisabled}
                    onChange={(e) => setPayerName(e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    {(payerNames[payerType] ?? []).map((n: string) => (
                      <MenuItem
                        key={n}
                        value={n}
                      >
                        {n}
                      </MenuItem>
                    ))}
                  </TextField>
                </span>
              </Tooltip>

              <TherapistSelect
                therapists={therapists}
                loading={therapistsLoading}
                therapistUuid={therapistUuid}
                onTherapistChange={setTherapistUuid}
              />
            </Stack>

            <TherapistScopeToggle
              therapists={therapists}
              therapistUuid={therapistUuid}
              scope={scope}
              onScopeChange={setScope}
              authorCount={scopeCounts.author}
              chartsCount={scopeCounts.charts}
            />
          </Stack>
        </Step>

        <Step
          n={3}
          title="Documents"
        >
          <GroupPicker
            groups={groups}
            loading={summaryLoading}
            deselected={deselectedKinds}
            onSelectAll={() => setDeselectedKinds(new Set())}
            onClear={() => setDeselectedKinds(new Set(groups.map((g) => g.kind)))}
            onToggle={(kind) =>
              setDeselectedKinds((prev) => {
                const next = new Set(prev);
                if (next.has(kind)) next.delete(kind);
                else next.add(kind);
                return next;
              })
            }
          />
          <HeldFromBillingToggle
            checked={includeHeld}
            onChange={setIncludeHeld}
            withheldCount={summary?.held_withheld_count ?? 0}
          />
        </Step>

        {/* <Step
          n={4}
          title="Who matched"
          hint="the roster is an output, not an input"
        >
          <MatchedRoster
            patients={patients}
            loading={patientsLoading}
            deselected={deselectedPatients}
            onToggle={(id) =>
              setDeselectedPatients((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onToggleAll={(selectAll) =>
              setDeselectedPatients(
                selectAll ? new Set() : new Set(patients.map((p) => p.patient_id))
              )
            }
          />
        </Step> */}
      </Stack>

      <Box
        sx={{
          flex: { md: '0 0 calc(40% - 12px)' },
          width: '100%',
          minWidth: 0,
          position: { md: 'sticky' },
          top: { md: 24 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2.5,
        }}
      >
        <PacketSummary
          patientLabel={`${selectedPatients.length} residents`}
          patients={selectedPatients.map((p) => ({
            id: p.patient_id,
            label: `${p.last_name}, ${p.first_name}`,
            subLabel: p.mrn,
            count: p.document_count,
          }))}
          periodLabel={periodLabel}
          documentCount={selectedDocuments}
          choices={choices}
          fileName={fileName}
          onFileNameChange={(v) => {
            setFileName(v);
            setFileNameEdited(true);
          }}
          adrReference={adrReference}
          onAdrReferenceChange={setAdrReference}
          onReview={() => setReviewOpen(true)}
        />
      </Box>

      <ReviewAndRequestDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        choices={choices}
        onChange={setChoices}
        groups={
          groups.map((g) => ({
            kind: g.kind,
            label: g.label,
            documents: Array.from({ length: g.documentCount }, (_, i) => ({
              uuid: `${g.kind}-${i}`,
            })),
          })) as any
        }
        selected={
          new Set(
            groups
              .filter((g) => !deselectedKinds.has(g.kind))
              .flatMap((g) => Array.from({ length: g.documentCount }, (_, i) => `${g.kind}-${i}`))
          )
        }
        fileName={fileName}
        patientLabel={`${selectedPatients.length} residents`}
        submitting={submitting}
        onRequest={handleRequest}
      />
    </Stack>
  );
};